import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { AlertService } from '@services/alert.service';
import { LoadingService } from '@services/loading.service';
import { PopoverAction, PopoverOptions, PopoverService } from '@services/popover.service';

@Component({
  selector: 'app-popover-options',
  styleUrl: './popover-options.component.scss',
  templateUrl: './popover-options.component.html',
})
export class PopoverOptionsComponent<T> implements AfterViewInit {
  popover = input.required<PopoverOptions<T>>();
  private alert = inject(AlertService).get();
  private arrow = viewChild.required<ElementRef<HTMLElement>>('arrow');
  private loading = inject(LoadingService).get();
  private menu = viewChild.required<ElementRef<HTMLElement>>('menu');
  private popoverService = inject(PopoverService<T>);

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.positionMenu();
      if (!this.loading()) {
        this.getEnabledOptions()[0]?.focus();
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (this.alert() || this.loading()) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.popoverService.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    const options = this.getEnabledOptions();
    if (options.length === 0) {
      return;
    }
    event.preventDefault();
    const currentIndex = options.indexOf(document.activeElement as HTMLButtonElement);
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + offset + options.length) % options.length;
    options[nextIndex].focus();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.positionMenu();
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    this.popoverService.close();
  }

  protected close(): void {
    this.popoverService.close();
  }

  protected selectOption(action: PopoverAction<T>): void {
    if (action.disabled) {
      return;
    }
    this.popoverService.close(false);
    action.onClick(this.popover().data);
  }

  private getEnabledOptions(): HTMLButtonElement[] {
    return Array.from(this.menu().nativeElement.querySelectorAll<HTMLButtonElement>('.popover-option:not(:disabled)'));
  }

  private positionMenu(): void {
    const arrowHalfSize = 7;
    const arrowInset = 18;
    const margin = 8;
    const offset = 12;
    const anchorRect = this.popover().anchor.getBoundingClientRect();
    const arrowElement = this.arrow().nativeElement;
    const menuElement = this.menu().nativeElement;
    const menuRect = menuElement.getBoundingClientRect();
    const placement = this.popover().placement;
    const alignsEnd = placement.endsWith('end');
    let opensAbove = placement.startsWith('top');
    const desiredLeft = alignsEnd ? anchorRect.right - menuRect.width : anchorRect.left;
    const maximumLeft = window.innerWidth - menuRect.width - margin;
    let desiredTop = opensAbove ? anchorRect.top - menuRect.height - offset : anchorRect.bottom + offset;
    if (!opensAbove && desiredTop + menuRect.height > window.innerHeight - margin && anchorRect.top - menuRect.height - offset >= margin) {
      desiredTop = anchorRect.top - menuRect.height - offset;
      opensAbove = true;
    } else if (opensAbove && desiredTop < margin && anchorRect.bottom + menuRect.height + offset <= window.innerHeight - margin) {
      desiredTop = anchorRect.bottom + offset;
      opensAbove = false;
    }
    const maximumTop = window.innerHeight - menuRect.height - margin;
    const menuLeft = Math.max(margin, Math.min(desiredLeft, maximumLeft));
    const menuTop = Math.max(margin, Math.min(desiredTop, maximumTop));
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const arrowCenter = Math.max(menuLeft + arrowInset, Math.min(anchorCenter, menuLeft + menuRect.width - arrowInset));
    arrowElement.style.left = `${arrowCenter - arrowHalfSize}px`;
    arrowElement.style.top = `${opensAbove ? menuTop + menuRect.height - arrowHalfSize : menuTop - arrowHalfSize}px`;
    arrowElement.style.visibility = 'visible';
    menuElement.style.left = `${menuLeft}px`;
    menuElement.style.top = `${menuTop}px`;
    menuElement.style.visibility = 'visible';
  }
}
